import React, {Component} from "react"
import PropTypes from "prop-types"
import {Col} from "react-bootstrap"
import styled from "styled-components"
import moment from "moment"

import {FormattedMessage} from "@irati/utils/lang"
import authentication from "@irati/auth"
import theme from "@irati/ui/src/global/theme"
import firstnameLastname from "@irati/identity/helpers/user/firstnameLastname"
import Form from "@irati/ui/src/Form"
import {Checkbox} from "@irati/ui/src/FormItem"
import {Label} from "@irati/ui/src/Typography"
import {isGlobalAdmin} from "@irati/identity/helpers/user/role"
import {TeamAccessLevel} from "@irati-web/fields/user"
import TeamName from "@irati-web/platform/src/components/TeamName"
import ProfilePicture from "@irati/ui/src/ProfilePicture"
import {TeamAssignment, UserDateOfBirth} from "@irati/types"
import {canModifyUserMembership} from "@irati/identity/utils/team/membership"

import {Row, RowMiddle, RowStart, RowEnd, RowHr} from "../../Row"
import LabelRowItem from "./styled/LabelRowItem"

const SmallForm = styled(Form)`
  height: 25px;
`

const DeactivatedChip = styled.div`
  background-color: ${theme.colors.greys.dark};
  color: white;
  display: inline-block;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  padding: 2px 10px;
  border-radius: 15px;
`

class User extends Component {
  static propTypes = {
    checked: PropTypes.bool,
    user: PropTypes.object.isRequired,
    onToggle: PropTypes.func.isRequired,
    action: PropTypes.node.isRequired,
    rowData: PropTypes.string,
    className: PropTypes.string,
    showTeams: PropTypes.bool,
    showCheckbox: PropTypes.bool,
    lowercaseRowData: PropTypes.bool,
    team: PropTypes.object,
    businessUnit: PropTypes.string
  }

  static defaultProps = {
    className: null,
    rowData: null,
    checked: false,
    onToggle: () => {},
    showTeams: true,
    showCheckbox: true,
    lowercaseRowData: false
  }

  updateSelectedUser = () => {
    this.props.onToggle(this.props.user)
  }

  canModifyThisUser = () => {
    const {team, user} = this.props
    if (team && !canModifyUserMembership(team, authentication.user(), user)) {
      return false
    }

    return true
  }

  getUserDateOfBirth = (date?: UserDateOfBirth) => {
    if (date?.day && date?.month) {
      return date?.year
        ? moment([date.year, date.month - 1, date.day]).format("DD/MM/YYYY")
        : moment({month: date.month - 1, day: date.day}).format("DD/MM")
    }
    return ""
  }

  getUserExternalDate = (externalStartDate?: string) => {
    return externalStartDate ? moment(externalStartDate).format("DD/MM/YYYY") : ""
  }

  render() {
    const {user, checked, showTeams, showCheckbox, action, rowData, className, lowercaseRowData, businessUnit, team} = this.props
    const deactivated = !user.isEnabled
    const permissionToSelect = this.canModifyThisUser()
    const showAdminLabel = !team && isGlobalAdmin(authentication.business(), user)
    const isPrimaryTeam = user.primaryTeamId === team?.uuid
    const adminLabel = isPrimaryTeam ? "admin.components.user.adminRolePrimaryTeamLabel" : "admin.roles.admin"
    const currentTeamAssignment = user?.teamAssignments.find(({teamId}) => teamId === team?.uuid)?.assignment
    const isManagerOfAnyTeam = !!user?.teamAssignments.find((t) => t?.assignment === TeamAssignment.Manager)
    const showTeamManagerLabel = currentTeamAssignment === TeamAssignment.Manager || (!showAdminLabel && isManagerOfAnyTeam)

    return (
      <div className={className}>
        <RowHr />
        <Row>
          <RowStart>
            {showCheckbox && (
              <SmallForm>
                <Checkbox name={`check_${user.uuid}`} checked={checked} onChange={this.updateSelectedUser} />
              </SmallForm>
            )}
            <ProfilePicture user={user} deactivated={deactivated || !permissionToSelect} regular />
          </RowStart>
          <RowMiddle>
            <Col xs={2}>
              {showAdminLabel && (
                <Label color={theme.colors.greys.mediumDark}>
                  <FormattedMessage id={adminLabel} />
                </Label>
              )}
              {!showAdminLabel && isPrimaryTeam && (
                <Label color={theme.colors.greys.mediumDark}>
                  <FormattedMessage id="admin.components.user.primaryTeamLabel" />
                </Label>
              )}
              {showTeamManagerLabel && (
                <Label color={theme.colors.greys.mediumDark}>
                  <FormattedMessage id="admin.components.user.teamManagerLabel" />
                </Label>
              )}
              {deactivated && (
                <DeactivatedChip>
                  <FormattedMessage id="admin.components.user.deactivatedChip.text" />
                </DeactivatedChip>
              )}
              <LabelRowItem large deactivated={deactivated || !permissionToSelect}>
                {firstnameLastname(user)}
              </LabelRowItem>
            </Col>
            {team && (
              <Col xs={2}>
                <TeamAccessLevel teamId={team?.uuid} userId={user.uuid} value={currentTeamAssignment} />
              </Col>
            )}
            {showTeams && (
              <Col xs={2}>
                <LabelRowItem large lowercase={lowercaseRowData} deactivated={deactivated || !permissionToSelect}>
                  {rowData}
                </LabelRowItem>
              </Col>
            )}
            <Col xs={2}>
              {businessUnit && (
                <LabelRowItem large>
                  <TeamName team={businessUnit} />
                </LabelRowItem>
              )}
            </Col>
            <Col xs={2}>
              <LabelRowItem large>{this.getUserExternalDate(user.externalStartDate)}</LabelRowItem>
            </Col>
            <Col xs={2}>
              <LabelRowItem large>{this.getUserDateOfBirth(user.dateOfBirth)}</LabelRowItem>
            </Col>
          </RowMiddle>
          <RowEnd>{permissionToSelect && action}</RowEnd>
        </Row>
      </div>
    )
  }
}

export default User
